<template>
  <div>
    <h2>
      <span v-if="moduleType === 4">
        Analyse de cause
      </span>
      <span v-else-if="moduleType === 1">
        Observation des pratiques
      </span>
      <span v-else>
        Surveillance
      </span>
      <span class="label" ref="title" :class="`label-${module}`">
        {{ moduleName }}
      </span>
    </h2>
    <hr>
    <div class="row" >
      <div class="col-lg-10">
        <alert type="warning" class="text-justify">
          <btn size="xs" type="default" @click="helpCollapse = !helpCollapse"
               class="help-button">
            Aide
          </btn>
          <collapse v-model="helpCollapse">
            <h4>
              Bienvenue sur l'espace
              <span class="label" :class="`label-${module}`">{{ moduleName }}</span>
              du programme SPIADI {{ year }}
            </h4>
            <p>
              Le menu de navigation situé à gauche
              de l'écran vous permet de vous déplacer entre les différentes pages
              de cet espace.
            </p>
            <p>
              <router-link :to="`${routePrefix}/referents`">
                <span class="glyphicon glyphicon-user"
                      aria-hidden="true">
                </span>
                &nbsp;
                Membres
              </router-link>
              <br />
              Cette page contient la liste des membres pour cette modalité. Les
              membres autorisés peuvent ajouter, supprimer et modifier des membres
              dans cette liste.
            </p>
            <p v-if="canEditSheets">
              <router-link :to="`${routePrefix}/sheets`">
                <span class="glyphicon glyphicon-pencil"
                      aria-hidden="true">
                </span>
                &nbsp;
                Module de saisie
              </router-link>
              <br />
              Cette page permet d'ajouter, modifier et contrôler des fiches.
              <br />
              <strong v-if="year === '2019'">
                La surveillance étant terminée, les ajouts et modifications
                effectuées ne seront pas prises en compte dans le rapport
                national.
              </strong>
            </p>
            <p v-if="year === '2019'">
              <router-link :to="`${routePrefix}/data`">
                <span class="glyphicon glyphicon-stats"
                      aria-hidden="true">
                </span>
                &nbsp;
                Données
              </router-link>
              <br />
              Cette page permet d'exporter les fiches validées en csv,
              d'explorer les données et de générer des graphiques.
            </p>
          </collapse>
        </alert>
      </div>
    </div>
    <div v-if="loading">
      <sui-loading-text>Chargement</sui-loading-text>
    </div>
    <div class="row" v-else>
      <div class="col-xs-12">
        <div v-if="datasetPanelType === 0">
          <closure :surveillance="surveillance"
               type="surv"
               :year="year"
               :module="module"
               :datasets="datasets"
               :customDataset="customData"
               :sheets="all[module]">
          </closure>
        </div>
        <div v-else-if="datasetPanelType === 1">
          <closure :surveillance="surveillance"
               type="obs"
               :year="year"
               :module="module"
               :datasets="datasets"
               :customDataset="true"
               :sheets="all[module]">
          </closure>
        </div>
        <div v-else-if="datasetPanelType === 4">
          <closure :surveillance="surveillance"
               type="cause"
               :year="year"
               :module="module"
               :datasets="datasets"
               :customDataset="false"
               :sheets="all[module]">
          </closure>
        </div>
      </div>

      <div class="col-xs-12" v-if="datasetPanelType === -1">
        <alert type="info">
          <p class="text-justify">
            <strong>
              Votre inscription au module
              <span class="label" ref="title" :class="`label-${module}`">{{ moduleName }}</span>
              a bien été prise en compte.
            </strong>
          </p>
        </alert>
      </div>
    </div>

    <div v-if="display" class="row">
      <div class="col-xs-12 col-md-6 col-lg-5 chart-container">
        <chart type="line-chart"
               :config="evolutionConfig"></chart>
      </div>

      <div class="col-xs-12 col-md-6 col-lg-5 chart-container">
        <chart type="pie-chart"
               :config="pieConfig"></chart>
      </div>
    </div>
  </div>
</template>
<script>
import chart from '@/sui/components/charts/chart'
import modules from '@/sui/data/modules'
import closure from '@/sui/components/closure'
import suiLoadingText from '@/sui/components/loadingtext'
import validateSheets from '@/sui/components/sheet/validate/sheets'
import assert from '@/sui/tools/assert'

import { mapState, mapGetters } from 'vuex'

import moment from 'moment'

export default {
  name: 'sheet-dashboard',

  props: {
    surveillance: {
      type: Object
    },

    year: {
      type: String,
      required: true
    },

    module: {
      type: String,
      required: true
    }
  },

  data: function () {
    return {
      helpCollapse: this.year === '2023',
      isMounted: false
    }
  },

  mounted: function () {
    this.isMounted = true
  },

  computed: {
    ...mapState({
      all: state => state.sheet.all,
      available: state => state.sheet.available
    }),

    ...mapGetters({
      userId: 'user/id'
    }),

    customData: function () {
      const years = [
        '2020', '2021', '2022', '2023', '2024', '2025', '2026'
      ]
      return years.findIndex(v => v === this.year) !== -1
    },

    datasets: function () {
      const result = {
        spiadi: null,
        others: []
      }
      if (this.surveillance == null) {
        return result
      }

      const key = `SPIADI${this.year}`
      this.surveillance.modules[this.module].dset.forEach(
        d => {
          if (d.name === key) {
            result.spiadi = d
          } else {
            result.others.push(d)
          }
        }
      )
      return result
    },

    validatedSheets: function () {
      if (this.surveillance == null) {
        return null
      }

      const c = this.counters.repartition
      if (c != null && c.length > 0) {
        return c
      }

      return null
    },

    moduleName: function () {
      return modules[this.year][this.module].label
    },

    moduleColor: function () {
      return modules[this.year][this.module].color
    },

    moduleType: function () {
      return modules[this.year][this.module].type
    },

    routePrefix: function () {
      const { year, id } = this.$route.params
      return `/establishments/${year}/${id}/module/${this.module}`
    },

    canEditSheets: function () {
      if (this.surveillance == null) {
        return false
      }
      return this.surveillance.canEditSheets(this.userId, this.module)
    },

    display: function () {
      return this.all[this.module].length
    },

    loading: function () {
      return !this.available[this.module]
    },

    datasetPanelType: function () {
      const moduleType = this.moduleType
      if (!this.isMounted ||
          this.module === 'reaSink' ||
          this.module === 'reaExplaur' ||
          moduleType === modules.types.research ||
          moduleType === modules.types.results ||
          this.loading ||
          !this.canValidateSheets) {
        return -1
      }

      const year = parseInt(this.year)
      if (moduleType === modules.types.observation) {
        if (year >= 2021 && year <= 2026) {
          return moduleType
        }

        return -1
      }

      if (moduleType === modules.types.causeAnalysis) {
        assert(year === 2026)
        return moduleType
      }

      assert(moduleType === modules.types.surveillance)
      if (year >= 2020 && year <= 2026) {
        return moduleType
      }

      return -1
    },

    canValidateSheets: function () {
      return this.surveillance != null &&
             this.surveillance.canValidateSheets(this.userId, this.module)
    },

    counters: function () {
      const format = 'YYYYMMDD'
      const days = {}
      const count = this.all[this.module].length
      let open = 0
      for (let i = 0; i !== count; ++i) {
        const sheet = this.all[this.module][i]
        if (sheet.status === 'open') {
          ++open
        }
        const day = moment(sheet.creation).format(format)
        if (days[day] == null) {
          days[day] = 1
        } else {
          days[day]++
        }
      }
      const start = {
        '2019': '20190420',
        '2020': '20200310',
        '2021': '20210101',
        '2022': '20220101',
        '2023': '20230101'
      }
      const evolution = [
        {
          t: moment(start[this.year], format).local(), y: 0
        }
      ]
      Object.keys(days).forEach(
        key => {
          evolution.push({
            t: moment(key, format).local(),
            y: days[key]
          })
        }
      )
      evolution.sort((a, b) => { return a.t.valueOf() - b.t.valueOf() })
      let sum = 0
      evolution.forEach(
        e => {
          sum += e.y
          e.y = sum
          e.t = e.t.format()
        }
      )
      return {
        evolution: evolution,
        repartition: [ count - open, open ]
      }
    },

    evolutionConfig: function () {
      const base = '#0a77a7'
      const result = {
        data: {},
        options: {
          element: {
            line: {
              backgroundColor: 'red'
            }
          },

          layout: {
            padding: {
              left: 20,
              right: 20
            }
          },
          legend: {
            position: 'bottom',
            labels: {
              fontColor: base
            }
          },
          scales: {
            xAxes: [
              {
                type: 'time',
                time: {
                  displayFormats: {
                    day: 'DD/MM'
                  },
                  tooltipFormat: 'll'
                }
              }
            ]
          },
          title: {
            display: true,
            fontColor: base,
            fontSize: 14
          },

          plugins: {
            datalabels: {
              backgroundColor: function (context) {
                return context.dataset.backgroundColor
              },
              color: '#ffffff',
              borderRadius: 4,
              align: 'top',
              font: { weight: 'bold' },
              display: function (context) {
                const index = context.dataIndex
                const count = context.dataset.data.length
                return index === count - 1
              },
              formatter: function (value, context) {
                return value.y
              }
            }
          }
        }
      }

      if (!this.isMounted) {
        result.options.title.text = 'Chargement...'
        result.data.datasets = [
          {
            fill: false,
            label: 'Chargement',
            backgroundColor: '#cc0000',
            data: [
              { t: moment().add(-2, 'days').format(), y: 0 },
              { t: moment().add(-1, 'days').format(), y: 2 },
              { t: moment().format(), y: 15 }
            ]
          }
        ]
      } else {
        result.options.title.text = `Évolution du nombre de fiches saisies`
        result.data.datasets = [
          {
            fill: false,
            label: `Fiches ${this.moduleName}`,
            backgroundColor: this.moduleColor,
            borderColor: this.moduleColor,
            strokeColor: 'red',
            borderWidth: 5,
            borderCap: 'round',
            data: this.counters.evolution
          }
        ]
      }

      return result
    },

    pieConfig: function () {
      const base = '#0a77a7'
      const alphaBase = `${base}84`
      const result = {
        options: {
          title: {
            display: true,
            fontColor: base,
            fontSize: 14,
            text: 'Chargement...'
          },
          cutoutPercentage: 50,
          legend: {
            position: 'bottom',
            labels: {
              fontColor: base
            }
          },

          plugins: {
            datalabels: {
              backgroundColor: 'white',
              borderRadius: 3,
              borderColor: alphaBase,
              color: base,
              font: {
                weight: 'bold'
              },
              display: function (context) {
                const index = context.dataIndex
                const value = context.dataset.data[index]
                return value > 0
              },
              offset: 0
            }
          }
        },
        data: {}
      }

      if (!this.isMounted) {
        result.data.labels = [ 'Chargement...' ]
        result.data.datasets = [
          {
            fill: true,
            backgroundColor: [
              '#cccccc',
              '#cccccc44'
            ],
            data: [ 5, 1 ]
          }
        ]
      } else {
        result.options.title.text = 'Fiches saisies et fiches validées'
        result.data.labels = [ 'Fiches validées', 'Fiches en cours de saisie' ]
        result.data.datasets = [
          {
            fill: true,
            borderColor: this.moduleColor,
            backgroundColor: [
              this.moduleColor,
              `${this.moduleColor}64`
            ],
            data: this.counters.repartition
          }
        ]
      }
      return result
    }
  },

  components: {
    'chart': chart,
    'closure': closure,
    'sui-loading-text': suiLoadingText,
    'validate-sheets': validateSheets
  }
}
</script>
<style scoped lang="less">
@import '~variables';

.panel-title h3 {
  margin-top: 0rem;
  margin-bottom: 0rem;
}

.button-list {
  padding-bottom: 5rem;
}

.help-button {
  margin-top: -2.5rem;
  float: left;
}

.panel {
  .alert:last-child {
    margin-bottom: 0rem;
  }
}

.has-error .control-label {
  color: @brand-danger;
}

h5 {
  margin-top: 0rem;
  margin-bottom: 0rem;
}
</style>
